import { useState } from "react";
import { parseApiError } from "../api/errors";

const COUNTRY_CODES = [
  { code: "+1", name: "US/Canada", flag: "🇺🇸" },
  { code: "+44", name: "UK", flag: "🇬🇧" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+64", name: "New Zealand", flag: "🇳🇿" },
];

interface Props {
  title: string;
  // Helper copy under the title (e.g. "No code is sent — you're vouching").
  hint?: string;
  submitLabel?: string;
  // Resolve to close the dialog; throw to display the error inline.
  onSubmit: (e164: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Mobile-first modal for entering an E.164 phone number, mirroring the login
 * screen's country-code + number split. Reused for attaching/fixing a hasher's
 * number (Stories 2.1/2.2) and for the self-service change (Story 1.3).
 */
export function PhoneDialog({
  title,
  hint,
  submitLabel = "Save",
  onSubmit,
  onClose,
}: Props) {
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const e164 = countryCode + phone.replace(/\D/g, "");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onSubmit(e164);
    } catch (err) {
      // Unpack the "<status>: {json}" the api client throws into the clean
      // server message (e.g. the PHONE_CONFLICT "already attached to …" copy)
      // rather than dumping the raw status-prefixed blob at the user.
      setError(parseApiError(err, "Failed to save number").message);
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white p-5 shadow-xl sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:w-full sm:max-w-sm sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
      >
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="flex gap-2">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              aria-label="Country code"
              className="h-12 rounded-lg bg-slate-100 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              placeholder="555 123 4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="h-12 flex-1 rounded-lg bg-slate-100 px-4 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="mt-0.5 shrink-0"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700 active:bg-slate-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !phone.trim()}
              className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
